import React from "react";
import { Link } from "react-router-dom";
import { ScrollText, Shield, Zap, ArrowLeft, Mail } from "lucide-react";

const SECTIONS = [
  {
    id: "acceptance",
    title: "1. Acceptance of Terms",
    body: (
      <>
        <p>
          By accessing or using VideoLens (the “Service”), you agree to be bound by these
          Terms &amp; Conditions (the “Terms”). If you do not agree with any part of these
          Terms, you must not access or use the Service.
        </p>
        <p>
          You confirm that you are legally capable of entering into a binding agreement in the
          jurisdiction in which you reside.
        </p>
      </>
    ),
  },
  {
    id: "description",
    title: "2. Description of the Service",
    body: (
      <>
        <p>
          VideoLens is a web application that analyses publicly available YouTube videos
          (including Shorts) and produces informational insights about their creative
          structure, hook, pacing, audio, copy, call-to-action and related signals.
        </p>
        <p>
          All analysis results, scores and recommendations are informational in nature. They
          are not professional advice (marketing, financial, legal or otherwise) and should
          not be treated as guaranteed predictions of real-world performance.
        </p>
      </>
    ),
  },
  {
    id: "ai",
    title: "3. AI-Generated Results",
    body: (
      <>
        <p>
          Some outputs produced by the Service are generated or assisted by artificial
          intelligence. AI models can make mistakes.
        </p>
        <p>You acknowledge and agree that AI-generated analysis may:</p>
        <ul>
          <li>contain errors, inaccuracies or omissions;</li>
          <li>misinterpret context, intent or creative choices;</li>
          <li>produce results that differ between runs for the same input.</li>
        </ul>
        <p>
          You are solely responsible for independently reviewing and evaluating any output
          before relying on it to make business, advertising, financial or other decisions.
        </p>
      </>
    ),
  },
  {
    id: "no-guarantee",
    title: "4. No Guarantee of Results",
    body: (
      <>
        <p>
          The Service does not guarantee any particular outcome. In particular, we do not
          guarantee:
        </p>
        <ul>
          <li>advertising or campaign performance;</li>
          <li>views, impressions, watch-time or reach;</li>
          <li>clicks, click-through rate or engagement;</li>
          <li>conversions, leads or sign-ups;</li>
          <li>revenue, sales or profitability;</li>
          <li>virality, trend adoption or audience growth;</li>
          <li>any specific business or commercial result.</li>
        </ul>
        <p>
          Scores and recommendations are heuristic signals, not forecasts.
        </p>
      </>
    ),
  },
  {
    id: "accounts",
    title: "5. User Accounts",
    body: (
      <>
        <p>
          Certain features require an account. You agree to provide accurate information when
          registering and to keep that information up to date.
        </p>
        <p>
          You are responsible for maintaining the confidentiality of your login credentials
          and for all activity that occurs under your account. Please notify us promptly if
          you believe your account has been accessed without your authorisation.
        </p>
      </>
    ),
  },
  {
    id: "content",
    title: "6. User Content",
    body: (
      <>
        <p>
          You are solely responsible for any URLs, text, images, prompts or other material
          you submit to the Service (“User Content”).
        </p>
        <p>By submitting User Content you represent and warrant that:</p>
        <ul>
          <li>
            you have the necessary rights, licences or permissions to submit it and to have
            it processed by the Service;
          </li>
          <li>
            it does not infringe the intellectual-property, privacy or other rights of any
            third party;
          </li>
          <li>
            it is not unlawful, defamatory, obscene, hateful or otherwise objectionable; and
          </li>
          <li>it complies with these Terms.</li>
        </ul>
      </>
    ),
  },
  {
    id: "youtube",
    title: "7. YouTube and Third-Party Content",
    body: (
      <>
        <p>
          The Service analyses publicly available YouTube videos and metadata via the YouTube
          Data API. All YouTube videos, thumbnails, titles, descriptions, channel information
          and related content belong to their respective owners. VideoLens does not claim
          ownership of any third-party content.
        </p>
        <p>
          When using the Service to analyse YouTube content you must also comply with
          YouTube's Terms of Service, the Google Privacy Policy and any other applicable
          third-party platform terms.
        </p>
      </>
    ),
  },
  {
    id: "prohibited",
    title: "8. Prohibited Uses",
    body: (
      <>
        <p>You agree not to:</p>
        <ul>
          <li>use the Service for any unlawful or fraudulent purpose;</li>
          <li>
            attempt to gain unauthorised access to the Service, its servers, databases or
            related infrastructure;
          </li>
          <li>interfere with, disrupt or impair the operation of the Service;</li>
          <li>
            reverse engineer, decompile or otherwise attempt to derive source code or
            underlying ideas from the Service, except to the extent permitted by law;
          </li>
          <li>
            use scrapers, bots or other automated methods to overload or extract data from the
            Service;
          </li>
          <li>circumvent usage limits, rate limits or Premium-plan restrictions;</li>
          <li>
            use the Service to infringe intellectual-property, privacy, publicity or other
            rights;
          </li>
          <li>upload, submit or transmit malicious code, malware or harmful content.</li>
        </ul>
      </>
    ),
  },
  {
    id: "ip",
    title: "9. Intellectual Property",
    body: (
      <>
        <p>
          The Service, including its branding, name, logo, user interface, visual design,
          source code, text, graphics and other original material, is owned by VideoLens or
          its licensors and is protected by applicable intellectual-property laws.
        </p>
        <p>
          Subject to your compliance with these Terms, we grant you a limited, non-exclusive,
          non-transferable, revocable permission to use the Service for its intended purpose.
          No ownership rights are transferred to you.
        </p>
      </>
    ),
  },
  {
    id: "third-party",
    title: "10. Third-Party Services",
    body: (
      <>
        <p>
          The Service relies on third-party providers to operate, including (without
          limitation) hosting infrastructure, the YouTube Data API, large-language-model AI
          providers and payment processors such as Razorpay.
        </p>
        <p>
          These third-party services operate under their own terms and privacy policies. Their
          availability, pricing, functionality and policies may change without notice and are
          outside our direct control. We are not responsible for the actions or omissions of
          third-party providers.
        </p>
      </>
    ),
  },
  {
    id: "premium",
    title: "11. Premium Features and Payments",
    body: (
      <>
        <p>
          Certain features of the Service are offered as paid plans ("Premium"). Pricing,
          daily usage limits, billing cycles, autopay/mandate authorisation and the scope of
          each plan are shown in the Service at the time of purchase.
        </p>
        <p>
          Payments are processed by a third-party payment processor (currently Razorpay). By
          purchasing a Premium plan you also agree to the applicable terms of that payment
          processor. We do not store full card numbers or complete bank-account credentials on
          our servers.
        </p>
        <p>
          Subscription renewals, autopay authorisation, cancellations and refunds (if any) are
          governed by the plan details shown at the time of purchase and by the payment
          processor's policies. You may cancel an active subscription from your account
          settings; cancellation stops future renewals and does not necessarily entitle you to
          a refund of amounts already paid, except where required by applicable law.
        </p>
      </>
    ),
  },
  {
    id: "availability",
    title: "12. Availability and Changes",
    body: (
      <>
        <p>
          We may at any time, with or without notice, modify, update, suspend or discontinue
          the Service or any part of it, temporarily or permanently. Features may be added,
          changed or removed over time.
        </p>
        <p>
          We will try to inform users of material changes where reasonably practicable, but we
          are not obligated to do so unless required by applicable law.
        </p>
      </>
    ),
  },
  {
    id: "warranties",
    title: "13. Disclaimer of Warranties",
    body: (
      <>
        <p>
          To the fullest extent permitted by applicable law, the Service is provided on an
          <em> “as is” </em> and <em> “as available” </em> basis, without warranties of any
          kind, whether express, implied or statutory, including (but not limited to) implied
          warranties of merchantability, fitness for a particular purpose, accuracy or
          non-infringement.
        </p>
        <p>
          We do not warrant that the Service will be uninterrupted, error-free, secure or
          that defects will be corrected. Nothing in these Terms is intended to exclude or
          limit any rights that cannot be excluded or limited under applicable law.
        </p>
      </>
    ),
  },
  {
    id: "liability",
    title: "14. Limitation of Liability",
    body: (
      <>
        <p>
          To the maximum extent permitted by applicable law, in no event shall VideoLens, its
          affiliates, officers, employees or agents be liable for any indirect, incidental,
          special, consequential, punitive or exemplary damages, including lost profits, lost
          revenue, lost business opportunities, loss of goodwill or loss of data, arising out
          of or in connection with your use of (or inability to use) the Service, even if we
          have been advised of the possibility of such damages.
        </p>
        <p>
          Our aggregate liability in connection with the Service shall not exceed the greater
          of (a) the amounts actually paid by you to us for the Service during the three (3)
          months immediately preceding the event giving rise to the claim, or (b) the minimum
          amount required by applicable law. Nothing in these Terms excludes or limits
          liability for fraud, death or personal injury caused by negligence, or any other
          liability that cannot lawfully be excluded.
        </p>
      </>
    ),
  },
  {
    id: "indemnity",
    title: "15. Indemnification",
    body: (
      <>
        <p>
          To the extent permitted by applicable law, you agree to indemnify, defend and hold
          harmless VideoLens and its affiliates, officers, employees and agents from and
          against any claims, damages, losses, liabilities, costs and expenses (including
          reasonable legal fees) arising out of or related to:
        </p>
        <ul>
          <li>your unlawful or improper use of the Service;</li>
          <li>your violation of these Terms;</li>
          <li>
            your infringement of the intellectual-property, privacy or other rights of any
            third party; or
          </li>
          <li>any User Content you submit to the Service.</li>
        </ul>
      </>
    ),
  },
  {
    id: "termination",
    title: "16. Termination",
    body: (
      <>
        <p>
          We may suspend or terminate your access to the Service at any time, with or without
          notice, if we reasonably believe that you have breached these Terms or have misused
          the Service.
        </p>
        <p>
          You may stop using the Service at any time and may close your account from the
          settings area. Upon termination, provisions of these Terms that by their nature are
          intended to survive termination will continue to apply (for example, intellectual
          property, disclaimers, limitation of liability, indemnification and governing law).
        </p>
      </>
    ),
  },
  {
    id: "governing-law",
    title: "17. Governing Law",
    body: (
      <>
        <p>
          These Terms and any dispute or claim arising out of or in connection with them
          (including non-contractual disputes or claims) are governed by and construed in
          accordance with <strong>[Insert applicable governing law and jurisdiction]</strong>.
        </p>
        <p>
          You and VideoLens agree to submit to the exclusive jurisdiction of the courts of
          <strong> [Insert applicable governing law and jurisdiction] </strong> for the
          resolution of any such dispute, unless otherwise required by applicable law.
        </p>
      </>
    ),
  },
  {
    id: "changes",
    title: "18. Changes to These Terms",
    body: (
      <>
        <p>
          We may update these Terms from time to time. When we do, we will update the
          “Effective Date” shown below and post the revised Terms on the Service. Your
          continued use of the Service after an update means you accept the updated Terms. If
          you do not agree with the updated Terms, you must stop using the Service.
        </p>
      </>
    ),
  },
  {
    id: "contact",
    title: "19. Contact",
    body: (
      <>
        <p>
          If you have any questions about these Terms, you can reach us at:
          <br />
          <strong>[Insert official support email]</strong>
        </p>
      </>
    ),
  },
];

export default function Terms() {
  return (
    <div className="relative min-h-screen bg-background text-foreground">
      {/* Techy backdrop that matches the rest of the app */}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-[480px] grid-bg radial-fade opacity-30" />
      <div className="pointer-events-none absolute -top-40 -right-40 h-96 w-96 rounded-full bg-primary/20 blur-3xl" />

      {/* Top bar */}
      <header className="relative border-b border-border bg-card/40 backdrop-blur">
        <div className="mx-auto max-w-5xl flex items-center justify-between px-5 h-14">
          <Link to="/" className="flex items-center gap-2 group">
            <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary text-primary-foreground neon-primary">
              <Zap className="h-4 w-4" />
            </span>
            <span className="font-display text-base font-bold tracking-tight">
              Video<span className="gradient-text">Lens</span>
            </span>
          </Link>
          <Link
            to="/dashboard"
            data-testid="terms-back-link"
            className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back to app
          </Link>
        </div>
      </header>

      <main className="relative mx-auto max-w-3xl px-5 pt-10 pb-24 sm:pt-16" data-testid="terms-page">
        {/* Title */}
        <div className="mb-10">
          <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card/60 px-3 py-1 text-[11px] text-muted-foreground backdrop-blur">
            <ScrollText className="h-3.5 w-3.5 text-primary" />
            <span className="overline">Legal</span>
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight mt-4">
            Terms &amp; Conditions
          </h1>
          <p className="text-sm text-muted-foreground mt-3 max-w-xl">
            Please read these Terms carefully. They set out the rules for using VideoLens and
            describe what you can expect from the Service and what we expect from you.
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-2 text-[11px]">
            <span className="inline-flex items-center rounded-full border border-border bg-card/60 px-2.5 py-1 text-muted-foreground">
              <span className="overline mr-1">Effective&nbsp;Date</span>
              <span className="tabular">[Insert Date]</span>
            </span>
            <Link
              to="/privacy"
              data-testid="terms-privacy-link"
              className="inline-flex items-center gap-1 rounded-full border border-border bg-card/60 px-2.5 py-1 text-muted-foreground hover:text-foreground transition-colors"
            >
              <Shield className="h-3 w-3" /> Privacy Policy
            </Link>
            <a
              href="mailto:[Insert official support email]"
              className="inline-flex items-center gap-1 rounded-full border border-border bg-card/60 px-2.5 py-1 text-muted-foreground hover:text-foreground transition-colors"
            >
              <Mail className="h-3 w-3" /> Contact
            </a>
          </div>
        </div>

        {/* Table of contents */}
        <nav className="mb-10 rounded-2xl border border-border bg-card/40 backdrop-blur p-5">
          <p className="overline text-muted-foreground mb-3">On this page</p>
          <ol className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
            {SECTIONS.map((s) => (
              <li key={s.id}>
                <a
                  href={`#${s.id}`}
                  className="text-muted-foreground hover:text-primary transition-colors"
                >
                  {s.title}
                </a>
              </li>
            ))}
          </ol>
        </nav>

        {/* Sections */}
        <div className="space-y-10 legal-prose">
          {SECTIONS.map((s) => (
            <section
              key={s.id}
              id={s.id}
              data-testid={`terms-section-${s.id}`}
              className="scroll-mt-24"
            >
              <h2 className="font-display text-xl sm:text-2xl font-bold tracking-tight">{s.title}</h2>
              <div className="mt-3 space-y-3 text-sm sm:text-[15px] leading-relaxed text-foreground/90">
                {s.body}
              </div>
            </section>
          ))}
        </div>

        {/* Footer note */}
        <div className="mt-16 rounded-2xl border border-dashed border-border p-5 text-xs text-muted-foreground">
          <p>
            These Terms are provided as a general template and do not constitute legal advice.
            Please consult a qualified lawyer before publishing them, especially to fill in the
            governing-law section, the effective date and the official support contact.
          </p>
        </div>
      </main>
    </div>
  );
}
